import { useState } from 'react';
import { Alert, Image, Linking, Platform, Pressable, Text, View } from 'react-native';
import type { Anexo } from '../store/types';
import { abreAqui, abrirAnexo, anexoDeBytes, apagarAnexo, protegerAnexo, temLocal } from '../lib/anexos';
import { fotoReduzida } from '../lib/fotosPdf';
import { montarPdf, nomeArquivo, type BlocoPdf } from '../lib/pdf';
import { semBloqueio } from '../auth/AuthContext';
import { useAnexar } from './MenuAnexo';
import type { OrigemAnexo } from '../lib/anexar';
import { Chip, Field } from './ui';
import { C } from './theme';
import { t } from '../lib/i18n';
import { nomeAnexo } from '../lib/nomeArquivo';
import { abrirUrlComApp } from '../lib/abrirArquivo';
import { urlGrupo, urlGrupoJa, useUrlsGrupo } from '../lib/arquivosGrupo';

const erro = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));
export const iconeAnexo = (a: Anexo) => (a.cofre ? '🔐' : a.nuvem ? '☁️' : a.url ? '🔗' : a.mime?.startsWith('image/') ? '🖼️' : a.mime === 'application/pdf' ? '📄' : '📎');
const ehImagem = (a: Anexo) => !!a.mime?.startsWith('image/');
/** Endereço para mostrar a miniatura (imagem embutida ou guardada neste aparelho). */
export const fonteImagem = (a: Anexo) => (a.cofre ? undefined : a.dado ? a.dado : temLocal(a) ? a.uri : a.gp ? urlGrupoJa(a.gp) : undefined);
export { useUrlsGrupo };
/** Abre o anexo: guardado aqui, link, ou a cópia na pasta do grupo (fichas compartilhadas). */
export async function abrirQualquer(a: Anexo) {
  if (a.gp && !temLocal(a) && !a.url) {
    const u = await urlGrupo(a.gp);
    return abrirUrlComApp(u, a.mime, nomeAnexo(a));   // texto 24 (2): no navegador também abre a cópia, sem o endereço do servidor
  }
  return abrirAnexo(a);
}

/** Lista de anexos com os botões Fotos, Tirar foto, Arquivos e Link (0.72, texto 11g: mesmos nomes e o mesmo
 *  caminho do menu de anexos, components/MenuAnexo + lib/anexar; Fotos sem a galeria neste app mostra o aviso). Usado na tarefa e nas fichas das abas.
 *  rotulos: nome de cada anexo pode ganhar uma marca (ex.: Antes / Depois) com marcas=[...]. */
export function AnexosEditor({ lista, cor, onChange, marcas, aviso = true, protegido, pedirCofre, soVer, juntarFotos, nomePdf }:
  { lista: Anexo[]; cor: string; onChange: (l: Anexo[]) => void; marcas?: string[]; aviso?: boolean;
    protegido?: boolean; pedirCofre?: () => Promise<boolean>; soVer?: boolean;
    /** texto 27 (sobras): várias fotos viram UM PDF só (ex.: documento do cliente fotografado página por página) */
    juntarFotos?: boolean; nomePdf?: string }) {
  const [juntando, setJuntando] = useState(false);
  useUrlsGrupo();
  // item protegido: o arquivo não vai para a nuvem e é criptografado assim que o cofre estiver aberto
  const anexo = useAnexar({ cor, semNuvem: !!protegido });
  const [link, setLink] = useState('');
  const [abrirLink, setAbrirLink] = useState(false);
  async function abrir(a: Anexo) {
    try { await semBloqueio(() => abrirQualquer(a)); }
    catch (e: any) {
      if (String(e?.message).includes('cofre-fechado') && pedirCofre && (await pedirCofre())) return semBloqueio(() => abrirAnexo(a)).catch(erro);
      erro(e);
    }
  }

  // Fotos (várias de uma vez, com vídeos), Tirar foto ou Arquivos; o que foi escolhido entra nesta lista, nesta tela
  async function adicionar(origem: OrigemAnexo) {
    try {
      const r = await anexo.anexar({ origem, tipo: 'tudo', varios: origem === 'fotos' });
      if (!r) return;
      let l = [...lista];
      for (const a of [r.anexo, ...(r.outros ?? [])]) {
        let x = a;
        if (protegido && pedirCofre && (await pedirCofre())) { try { x = await protegerAnexo(a); } catch (e) { erro(e); } }
        l = [...l, x];
      }
      onChange(l);
      if (r.aviso) Alert.alert(t('Arquivo adicionado'), r.aviso);
    } catch (e) { erro(e); }
  }
  function addLink() {
    const u = link.trim();
    if (!/^https:\/\/\S+$/i.test(u)) return Alert.alert(t('O link deve começar com https://'));
    onChange([...lista, { nome: u.replace(/^https:\/\/(www\.)?/i, '').slice(0, 60), url: u }]);
    setLink(''); setAbrirLink(false);
  }
  const fotosSoltas = lista.filter(a => ehImagem(a) && !a.cofre);
  async function juntar() {
    const fotos = fotosSoltas;
    Alert.alert(t('Juntar {n} fotos num PDF?', { n: fotos.length }), t('As fotos viram as páginas de um PDF só, na ordem da lista. As fotos soltas saem desta ficha.'), [
      { text: t('Cancelar'), style: 'cancel' },
      { text: t('Juntar'), onPress: async () => {
        setJuntando(true);
        try {
          const blocos: BlocoPdf[] = [];
          let faltou = 0;
          for (const a of fotos) { const b = await fotoReduzida(a, 1600, 0.75); if (b) blocos.push({ t: 'imagem', bytes: b, altMax: 720 }); else faltou++; }
          if (!blocos.length) throw new Error(t('Não foi possível ler as fotos neste dispositivo.'));
          const bytes = await montarPdf(blocos);
          const nome = nomeArquivo((nomePdf || t('Documento')).replace(/\.pdf$/i, '')) + '.pdf';
          const pdf = await anexoDeBytes(nome, bytes, 'application/pdf');
          const usadas = new Set(fotos);
          const pos = lista.findIndex(a => usadas.has(a));
          const resto = lista.filter(a => !usadas.has(a));
          onChange([...resto.slice(0, pos), pdf, ...resto.slice(pos)]);
          if (faltou) Alert.alert(t('PDF criado'), faltou === 1 ? t('1 foto não pôde ser lida neste dispositivo e ficou de fora.') : t('{n} fotos não puderam ser lidas neste dispositivo e ficaram de fora.', { n: faltou }));
        } catch (e) { erro(e); } finally { setJuntando(false); }
      } }]);
  }
  const marcar = (i: number, m: string) => onChange(lista.map((a, j) => (j === i ? { ...a, nome: `${m} · ${a.nome.replace(/^[^·]+ · /, '')}` } : a)));

  return (
    <View style={{ gap: 8 }}>
      {lista.map((a, i) => {
        const img = ehImagem(a) ? fonteImagem(a) : undefined;
        return (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 12, padding: 8 }}>
            {img ? <Image source={{ uri: img }} style={{ width: 44, height: 44, borderRadius: 8, backgroundColor: C.card2 }} />
              : <Text style={{ fontSize: 20, width: 44, textAlign: 'center' }}>{iconeAnexo(a)}</Text>}
            <Pressable style={{ flex: 1 }} onPress={() => abrir(a)}>
              <Text style={{ color: C.text }} numberOfLines={1}>{nomeAnexo(a)}</Text>
              {!abreAqui(a) && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Guardado em outro dispositivo')}</Text>}
              {!!a.cofre && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Criptografado no cofre, só neste dispositivo')}</Text>}
              {!!marcas && !soVer && (
                <View style={{ flexDirection: 'row', gap: 6, marginTop: 4 }}>
                  {marcas.map(m => <Chip key={m} small cor={cor} on={a.nome.startsWith(m + ' · ')} label={m} onPress={() => marcar(i, m)} />)}
                </View>
              )}
            </Pressable>
            {!soVer && <Pressable hitSlop={8} onPress={() => { apagarAnexo(a); onChange(lista.filter((_, j) => j !== i)); }}><Text style={{ color: C.mute, fontSize: 16 }}>✕</Text></Pressable>}
          </View>
        );
      })}
      {soVer && !lista.length && <Text style={{ color: C.mute, fontSize: 12 }}>{t('Nenhum arquivo.')}</Text>}
      {juntarFotos && !soVer && fotosSoltas.length >= 2 && (
        <Pressable onPress={juntar} disabled={juntando} style={{ alignSelf: 'flex-start', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 12, backgroundColor: C.card2 }}>
          <Text style={{ color: cor, fontWeight: '700', fontSize: 13 }}>{juntando ? t('Montando o PDF...') : '📄 ' + t('Juntar as {n} fotos num PDF', { n: fotosSoltas.length })}</Text>
        </Pressable>
      )}
      {!soVer && <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        <Chip cor={cor} on={false} label={'🖼️ ' + t('Fotos')} onPress={() => adicionar('fotos')} />
        {Platform.OS !== 'web' && <Chip cor={cor} on={false} label={'📷 ' + t('Tirar foto')} onPress={() => adicionar('camera')} />}
        <Chip cor={cor} on={false} label={'📎 ' + t('Arquivos')} onPress={() => adicionar('arquivos')} />
        <Chip cor={cor} on={abrirLink} label={'🔗 ' + t('Link')} onPress={() => setAbrirLink(x => !x)} />
      </View>}
      {abrirLink && (
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Field value={link} onChangeText={setLink} placeholder={t('Link (Drive, OneDrive, Dropbox)')} autoCapitalize="none" autoCorrect={false} style={{ flex: 1 }} onSubmitEditing={addLink} />
          <Chip cor={cor} on={false} label={t('Adicionar')} onPress={addLink} />
        </View>
      )}
      {aviso && !soVer && <Text style={{ color: C.mute, fontSize: 11 }}>{protegido ? '🔐 ' + t('Item protegido: fotos e arquivos ficam criptografados no cofre, só neste dispositivo (não vão para o Drive ou OneDrive).')
        : t('Fotos e arquivos ficam neste dispositivo (e no seu Drive ou OneDrive, se estiver ligado em Ajustes).')}</Text>}
      {anexo.tela}
    </View>
  );
}
